import { useEffect, useState } from 'react';
import api, { formatApiError } from '@/lib/api';
import { toast } from 'sonner';

import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';

import {
  UserCircle,
  Mail,
  Phone,
  Users,
  Clock,
  CheckCircle2,
  Loader2
} from 'lucide-react';

const initialForm = {
  nome: '',
  parentesco: '',
  whatsapp: '',
  email: ''
};

export default function AlunoResponsavelPage() {
  const [form, setForm] = useState(initialForm);
  const [solicitacao, setSolicitacao] = useState(null);
  const [loading, setLoading] = useState(true);
  const [salvando, setSalvando] = useState(false);

  const carregar = async () => {
    try {
      const response = await api.get(
        '/responsavel/minha-solicitacao'
      );

      const dados = response.data || null;

      setSolicitacao(dados);

      if (dados?.status === 'recusada') {
        setForm({
          nome: dados.responsavel_nome || '',
          parentesco: dados.parentesco || '',
          whatsapp: dados.responsavel_whatsapp || '',
          email: dados.responsavel_email || ''
        });
      }
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    carregar();
  }, []);

  const alterar = (campo, valor) => {
    setForm(prev => ({
      ...prev,
      [campo]: valor
    }));
  };

  const enviar = async (e) => {
    e.preventDefault();

    if (!form.nome.trim()) {
      toast.error('Informe o nome do responsável.');
      return;
    }

    if (!form.email.trim()) {
      toast.error('Informe o e-mail do responsável.');
      return;
    }

    setSalvando(true);

    try {
      await api.post(
        '/responsavel/solicitar',
        {
          nome: form.nome.trim(),
          parentesco: form.parentesco.trim(),
          whatsapp: form.whatsapp.trim(),
          email: form.email.trim()
        }
      );

      toast.success(
        'Responsável enviado para aprovação do gestor.'
      );

      setForm(initialForm);

      await carregar();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setSalvando(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center gap-2 text-zinc-400">
        <Loader2 className="w-4 h-4 animate-spin" />
        Carregando responsável...
      </div>
    );
  }

  const aprovado =
    solicitacao?.status === 'aprovada';

  const pendente =
    solicitacao?.status === 'pendente';

  const recusada =
    solicitacao?.status === 'recusada';

  if (aprovado || pendente) {
    return (
      <div className="space-y-6 animate-fadeIn">
        <div>
          <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
            Família
          </p>

          <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white mt-1">
            Meu Responsável
          </h1>

          <p className="text-sm text-zinc-500 mt-1">
            Acompanhe os dados e o status do seu responsável.
          </p>
        </div>

        <Card className="bg-[#141414] border-[#27272A]">
          <CardContent className="p-6">
            <div className="flex flex-col sm:flex-row sm:items-start gap-4">
              <div className="w-14 h-14 rounded-2xl bg-[var(--cor-primaria,#007AFF)]/10 border border-[var(--cor-primaria,#007AFF)]/20 flex items-center justify-center shrink-0">
                <UserCircle className="w-8 h-8 text-[var(--cor-primaria,#007AFF)]" />
              </div>

              <div className="flex-1">
                <div className="flex flex-wrap items-center gap-2">
                  <h2 className="text-xl font-bold text-white">
                    {solicitacao.responsavel_nome || 'Responsável'}
                  </h2>

                  {aprovado ? (
                    <Badge className="bg-green-500/10 text-green-400 border-green-500/20">
                      <CheckCircle2 className="w-3.5 h-3.5 mr-1" />
                      Aprovado
                    </Badge>
                  ) : (
                    <Badge className="bg-yellow-500/10 text-yellow-400 border-yellow-500/20">
                      <Clock className="w-3.5 h-3.5 mr-1" />
                      Aguardando aprovação
                    </Badge>
                  )}
                </div>

                {pendente && (
                  <p className="text-sm text-zinc-500 mt-2">
                    O gestor precisa analisar e aprovar este cadastro
                    antes que o responsável tenha acesso ao sistema.
                  </p>
                )}

                {aprovado && (
                  <p className="text-sm text-zinc-500 mt-2">
                    Seu responsável já está vinculado à sua conta.
                  </p>
                )}
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mt-6">
              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
                <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider">
                  <Users className="w-4 h-4" />
                  Parentesco
                </div>

                <p className="text-white mt-2">
                  {solicitacao.parentesco || 'Não informado'}
                </p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4">
                <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider">
                  <Phone className="w-4 h-4" />
                  WhatsApp
                </div>

                <p className="text-white mt-2">
                  {solicitacao.responsavel_whatsapp || 'Não informado'}
                </p>
              </div>

              <div className="bg-[#0A0A0A] border border-[#27272A] rounded-xl p-4 md:col-span-2">
                <div className="flex items-center gap-2 text-zinc-500 text-xs uppercase tracking-wider">
                  <Mail className="w-4 h-4" />
                  E-mail
                </div>

                <p className="text-white mt-2 break-all">
                  {solicitacao.responsavel_email || 'Não informado'}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div>
        <p className="text-xs uppercase tracking-[0.2em] text-zinc-500 font-bold">
          Família
        </p>

        <h1 className="text-3xl font-black font-['Barlow_Condensed'] uppercase text-white mt-1">
          Meu Responsável
        </h1>

        <p className="text-sm text-zinc-500 mt-1 max-w-2xl">
          Cadastre a pessoa responsável por acompanhar seus
          treinos, presença, mensalidades e comunicados.
          O acesso será liberado somente após aprovação do gestor.
        </p>
      </div>

      {recusada && (
        <Card className="bg-red-500/5 border-red-500/20">
          <CardContent className="p-5">
            <p className="text-sm font-semibold text-red-300">
              Solicitação recusada
            </p>

            <p className="text-sm text-zinc-400 mt-2">
              {solicitacao.motivo_recusa ||
                'O gestor recusou a solicitação anterior.'}
            </p>

            <p className="text-sm text-zinc-500 mt-2">
              Corrija os dados abaixo e envie novamente para aprovação.
            </p>
          </CardContent>
        </Card>
      )}

      <Card className="bg-[#141414] border-[#27272A]">
        <CardContent className="p-6">
          <form onSubmit={enviar} className="space-y-5">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label className="text-zinc-300">
                  Nome completo
                </Label>

                <Input
                  value={form.nome}
                  onChange={e => alterar('nome', e.target.value)}
                  placeholder="Nome do responsável"
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">
                  Parentesco
                </Label>

                <Input
                  value={form.parentesco}
                  onChange={e =>
                    alterar('parentesco', e.target.value)
                  }
                  placeholder="Ex.: Mãe, Pai, Avó..."
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">
                  WhatsApp
                </Label>

                <Input
                  value={form.whatsapp}
                  onChange={e =>
                    alterar('whatsapp', e.target.value)
                  }
                  placeholder="(99) 99999-9999"
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>

              <div className="space-y-2">
                <Label className="text-zinc-300">
                  E-mail
                </Label>

                <Input
                  type="email"
                  value={form.email}
                  onChange={e =>
                    alterar('email', e.target.value)
                  }
                  placeholder="email@exemplo.com"
                  className="bg-[#0A0A0A] border-[#27272A]"
                />
              </div>
            </div>

            <div className="rounded-xl border border-yellow-500/20 bg-yellow-500/5 p-4">
              <p className="text-sm text-yellow-200">
                Após o envio, o cadastro ficará aguardando
                aprovação do gestor da escola.
              </p>
            </div>

            <Button
              type="submit"
              disabled={salvando}
              className="bg-[var(--cor-primaria,#007AFF)] hover:opacity-90"
            >
              {salvando ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Enviando...
                </>
              ) : (
                'Enviar responsável para aprovação'
              )}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}
